import React from 'react'

import { Box, Stack } from '@mui/material'

import { Card, Skeleton } from '@repo/ui'
import { Header, Table } from 'components'
import { vhMinus } from 'utils/textZoom'

/**
 * Geometry of the role members table's columns — field, header, width, pinning.
 *
 * Declared HERE and spread into `RolePage`'s `memberColumns` (which adds the `renderCell`s) so the
 * skeleton's table and the real one cannot disagree about column widths. The skeleton needs exactly
 * this and nothing else: `Table`'s loading overlay sizes its placeholder cells from the visible
 * columns' widths, and no cell is rendered while loading.
 *
 * It lives in the skeleton rather than the page because the page imports the skeleton — the reverse
 * edge would be a circular import, which in this repo resolves to `undefined` at module-init.
 */
export const ROLE_MEMBER_COLUMNS = [
  { field: 'user', headerName: 'User', pinned: 'left' as const, disableReorder: true, width: 280 },
  { field: 'email', headerName: 'Email', type: 'string', width: 240 },
  {
    field: 'actions',
    headerName: false,
    align: 'center' as const,
    width: 56,
    pinned: 'right' as const,
  },
]

/**
 * Loading placeholder for the role detail page (§19).
 *
 * ## What it replaces
 *
 * `if (isLoading) return <Loader />` — a centred spinner in place of a 118px header and a
 * two-column body that fills the viewport.
 *
 * ## Measured, not guessed
 *
 * Off `/settings/company/roles/:name` (viewport 1711, content column x=280 w=1407):
 *
 *   root Stack       y=24  h=1256   — `vhMinus(48)`, i.e. CSS-driven, not content-driven
 *   wayfinding row   y=24  h=33
 *   title row        y=69  h=49
 *   header total     y=24  h=118
 *   header actions   Duplicate 122 / Assign users 141, h=40
 *   body row         y=166 h=1114   — `flex: 1`, so also CSS-driven
 *   members table    x=280 w=931, `fillParent`
 *   right column     x=1235 w=452: Details card 146 → gap 12 → Permissions card 956
 *   Details card     146 = 50 padding + 28 title + 24 + 44 (label 18 + 8 + value 18)
 *
 * ## Most of this page's height is CSS, which is why parity is easy here
 *
 * The root is `vhMinus(48)` and the body row is `flex: 1`, so the page's total footprint does not
 * depend on content at all — reproducing the same two containers reproduces the same height. Inside
 * them, the members table is the REAL `Table` in its `loading` state with the real column geometry
 * (shared via `ROLE_MEMBER_COLUMNS` above) and the same `fillParent`, so its 931x1114 comes from the
 * same CSS flex the loaded one uses. The Permissions card is a `Card` with `flex: 1`, so its 956 is
 * whatever is left after the Details card — again the loaded page's own arithmetic.
 *
 * The only measured literal is the Details card's 44px field block, and the two action widths.
 *
 * ## Where it deliberately under-reserves
 *
 * `Delete` renders only for a CUSTOM role, and `Assign users` only with the `assign_users`
 * permission — neither is knowable before the matrix fetch returns. The reserved set is the common
 * case: a built-in role viewed by someone who can assign.
 */
export const RolePageSkeleton = () => {
  const ActionSkeleton = ({ width }: { width: number }) => (
    <Skeleton variant="rounded" width={width} height={40} sx={{ borderRadius: 2 }} />
  )

  return (
    <Stack spacing={6} sx={{ height: vhMinus(48) }}>
      <Header
        // 96px is the measured width of a representative role name ("Admin" plus its chip sits in a
        // 155px group); the bar itself is the title's share of that.
        title={<Skeleton width={96} height={31} />}
        breadcrumbsOveride={<Skeleton width={150} height={16} />}
        status={null as any}
        rightComponents={
          <Stack direction="row" spacing={2} alignItems="center">
            {/* Delete is omitted — it renders only for a custom role. */}
            <ActionSkeleton width={122} />
            <ActionSkeleton width={141} />
          </Stack>
        }
      />

      <Stack direction="row" spacing={6} alignItems="stretch" sx={{ flex: 1, minHeight: 0 }}>
        {/* The real table in its loading state, same `fillParent`, same column widths. */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowX: 'hidden' }}>
          <Table
            fillParent
            loading
            rows={[]}
            columns={ROLE_MEMBER_COLUMNS}
            showSelection={false}
          />
        </Box>

        <Stack
          sx={{
            width: 452,
            flexShrink: 0,
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
          spacing={3}
        >
          {/* Details: the real Card with its real title, then the Role name field block (label 18
              + the 8px FieldLabel gap + an 18px value line) = the measured 44. */}
          <Card title="Details" sx={{ flexShrink: 0 }}>
            <Stack spacing={3}>
              <Box>
                <Box sx={{ height: 18, display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Skeleton width={72} height={14} />
                </Box>
                <Box sx={{ height: 18, display: 'flex', alignItems: 'center' }}>
                  <Skeleton width={120} height={14} />
                </Box>
              </Box>
            </Stack>
          </Card>

          {/* Permissions: `flex: 1` takes whatever the Details card leaves, exactly as the loaded
              card does — so its 956px is not a number reproduced here, it is the same arithmetic. */}
          <Card title="Permissions" sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Stack spacing={4}>
              {Array.from({ length: 10 }).map((_, i) => (
                <Stack
                  key={i}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={3}
                >
                  <Skeleton width={i % 3 === 0 ? 150 : 190} height={14} />
                  <Skeleton variant="rounded" width={34} height={20} sx={{ borderRadius: '999px' }} />
                </Stack>
              ))}
            </Stack>
          </Card>
        </Stack>
      </Stack>
    </Stack>
  )
}
